import { useEffect, useState } from "react";
import AdminLayout, { StatusBadge } from "../../components/admin/AdminLayout";
import { adminApi } from "../../lib/admin-api";
import { Save, ExternalLink, ChevronDown, AlertCircle } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";

const TABS = [
    { value: "eclipse", label: "Eclipse" },
    { value: "flexshade", label: "FlexShade" },
    { value: "velarias", label: "Velarias" },
];
const AW_ID_RE = /^AW-\d{6,}$/i;
const STATUS_BADGES = {
    sent: { label: "Enviado", color: "bg-green-100 text-green-700 border-green-200" },
    skipped: { label: "Omitido", color: "bg-neutral-100 text-neutral-600 border-neutral-200" },
    failed: { label: "Falló", color: "bg-red-100 text-red-700 border-red-200" },
};

export default function AdminAdsSetup() {
    const [tab, setTab] = useState("eclipse");
    const [configs, setConfigs] = useState({});
    const [conversions, setConversions] = useState([]);
    const [saving, setSaving] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [s, g] = await Promise.all([
                    adminApi.get("/settings/ads"),
                    adminApi.get("/gads/conversions", { params: { limit: 20 } }),
                ]);
                if (!alive) return;
                setConfigs(s.data || {});
                setConversions(g.data?.items || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => { alive = false; };
    }, []);

    const cfg = configs[tab] || {};
    const setField = (k, v) => setConfigs({ ...configs, [tab]: { ...cfg, [k]: v } });

    const idValid = !cfg.enabled || (cfg.google_ads_id && AW_ID_RE.test(cfg.google_ads_id));

    const save = async () => {
        if (cfg.enabled && !idValid) {
            toast.error("ID de Google Ads inválido (debe ser AW-XXXXXXXXX)");
            return;
        }
        setSaving(true);
        try {
            await adminApi.put("/settings/ads", {
                sitio: tab,
                google_ads_id: cfg.google_ads_id || null,
                conversion_label_form: cfg.conversion_label_form || "",
                conversion_label_catalog: cfg.conversion_label_catalog || "",
                conversion_label_whatsapp: cfg.conversion_label_whatsapp || "",
                conversion_label_phone: cfg.conversion_label_phone || "",
                enabled: !!cfg.enabled,
            });
            toast.success(`Configuración guardada para ${tab}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const bannerColor = cfg.enabled && idValid ? "bg-green-50 border-green-200 text-green-800" : cfg.enabled && !idValid ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800";
    const bannerText = cfg.enabled && idValid ? `✓ Google Ads activo: ${cfg.google_ads_id}` : cfg.enabled && !idValid ? "⚠ ID malformado — el tracking NO se cargará" : "○ Google Ads desactivado para este sitio";

    return (
        <AdminLayout title="Google Ads" subtitle="Configura tu seguimiento de conversiones por sitio (server-side + client-side)" testid="admin-adssetup-page">
            <div className="flex gap-1 bg-neutral-100 p-0.5 rounded w-fit" data-testid="adssetup-tabs">
                {TABS.map((t) => (
                    <button key={t.value} onClick={() => setTab(t.value)} className={`px-4 py-1.5 text-xs rounded ${tab === t.value ? "bg-white shadow-sm font-semibold" : "text-neutral-500"}`} data-testid={`adssetup-tab-${t.value}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className={`border rounded-md p-4 text-sm font-medium ${bannerColor}`} data-testid="adssetup-status-banner">
                {bannerText}
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="text-xs font-semibold text-neutral-600">ID de Google Ads</label>
                        <Input value={cfg.google_ads_id || ""} onChange={(e) => setField("google_ads_id", e.target.value)} placeholder="AW-XXXXXXXXX" data-testid="adssetup-awid" />
                        {cfg.google_ads_id && !AW_ID_RE.test(cfg.google_ads_id) && (
                            <div className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Formato inválido (esperado: AW- seguido de 6+ dígitos)</div>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs font-semibold text-neutral-600 mb-2">Activar tracking</label>
                        <div className="flex items-center gap-3 h-9">
                            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setField("enabled", v)} data-testid="adssetup-enabled-toggle" />
                            <span className="text-xs text-neutral-500">{cfg.enabled ? "Activo" : "Desactivado"}</span>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { key: "conversion_label_form", label: "Etiqueta — Formulario" },
                        { key: "conversion_label_catalog", label: "Etiqueta — Descarga catálogo" },
                        { key: "conversion_label_whatsapp", label: "Etiqueta — Click WhatsApp" },
                        { key: "conversion_label_phone", label: "Etiqueta — Click Teléfono" },
                    ].map((f) => (
                        <div key={f.key}>
                            <label className="text-xs font-semibold text-neutral-600">{f.label}</label>
                            <Input value={cfg[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} placeholder="abc123-XYZ_456" data-testid={`adssetup-${f.key}`} />
                        </div>
                    ))}
                </div>
                <div>
                    <button onClick={save} disabled={saving} className="h-10 px-5 bg-eclipse-cta text-white text-sm font-semibold rounded-md flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50" data-testid="adssetup-save-btn">
                        <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar configuración"}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md">
                <button onClick={() => setGuideOpen(!guideOpen)} className="w-full px-5 py-4 flex justify-between items-center text-left font-semibold text-sm hover:bg-neutral-50" data-testid="adssetup-guide-toggle">
                    <span>Guía paso a paso — Cómo obtener tu AW-ID y labels</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
                </button>
                {guideOpen && (
                    <ol className="px-6 pb-5 space-y-3 text-sm text-neutral-700 list-decimal list-inside">
                        <li>Ve a <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="text-eclipse-cta hover:underline inline-flex items-center gap-1">Google Ads <ExternalLink className="w-3 h-3" /></a> y entra a tu cuenta.</li>
                        <li>En el menú <strong>Herramientas → Conversiones</strong>, crea una nueva acción de conversión tipo "Sitio web".</li>
                        <li>Repite para cada tipo: <em>Formulario</em>, <em>Catálogo</em>, <em>WhatsApp</em>, <em>Teléfono</em>.</li>
                        <li>Cuando termines, Google te dará un fragmento de código tipo <code className="bg-neutral-100 px-1 rounded">gtag('event','conversion',{`{send_to:'AW-XXX/abc123'}`})</code>.</li>
                        <li>Copia el <strong>AW-XXXXXXXXX</strong> y la etiqueta (después del <code>/</code>) de cada conversión y pégalos arriba en este panel.</li>
                    </ol>
                )}
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                <div className="px-5 py-3 border-b border-neutral-100 flex justify-between items-center">
                    <h3 className="text-sm font-semibold text-neutral-900">Conversiones server-side (últimas 20)</h3>
                    <span className="text-xs text-neutral-500">{conversions.length} registradas</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-neutral-600 text-[11px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Sitio</th>
                                <th className="text-left px-4 py-3">Tipo</th>
                                <th className="text-left px-4 py-3">GCLID</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {conversions.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-neutral-400 text-xs">Aún no hay conversiones server-side. Se registran automáticamente cuando llega un lead con utm_medium=cpc o gclid.</td></tr>}
                            {conversions.slice(0, 20).map((c) => {
                                const st = STATUS_BADGES[c.status] || { label: c.status, color: "bg-neutral-100" };
                                return (
                                    <tr key={c.id} className="border-t border-neutral-100" data-testid={`gads-conv-${c.id}`}>
                                        <td className="px-4 py-2.5 text-neutral-500 text-xs">{new Date(c.created_at).toLocaleString("es-MX")}</td>
                                        <td className="px-4 py-2.5">{c.sitio}</td>
                                        <td className="px-4 py-2.5">{c.conversion_type}</td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-neutral-500">{(c.gclid || "—").slice(0, 14)}…</td>
                                        <td className="px-4 py-2.5"><StatusBadge label={st.label} color={st.color} /></td>
                                        <td className="px-4 py-2.5 text-xs text-neutral-500">{c.error || (c.response?.method || "—")}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}
